import React, { useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { EmailIDsField } from 'components/DataEntry/Form/fields/EmailIDsField';

import { CheckboxField } from '../fields/CheckboxField';
import { ArrayField, ObjectField } from '../fields/Containers';
import { CustomField } from '../fields/CustomField';
import { DayPickerField } from '../fields/DayPickerField';
import { DisplayField } from '../fields/DisplayField';
import { NumberField } from '../fields/NumberField';
import { RadioField } from '../fields/RadioField';
import { ReactSelectField } from '../fields/ReactSelectField';
import { SelectField } from '../fields/SelectField';
import { SwitchField } from '../fields/SwitchField';
import { TextAreaField } from '../fields/TextAreaField';
import { TextField } from '../fields/TextField';
import { TimePickerField } from '../fields/TimePickerField';
import { Field } from '../types';

const FieldWrapper = ({
  name,
  field,
  Component,
  isLoading,
  id,
}: {
  name: string;
  field: Field;
  Component: any;
  isLoading: boolean;
  id?: string;
}) => {
  const { watch } = useFormContext();
  const values = watch();

  const isVisible = useMemo(() => {
    return field.shouldDisplay ? field.shouldDisplay(values) : true;
  }, [values, field]);

  if (!isVisible) return null;

  if (field.type === 'custom') {
    const visible = field.isVisible !== undefined ? field.isVisible : true;
    if (!visible) return null;

    return (
      <Box key={`${name}-container`} className={name}>
        <Component
          name={name}
          id={id}
          data-testid={id}
          field={field}
          {...field.props}
          isLoading={isLoading}
        />
      </Box>
    );
  }

  return (
    <Box key={`${name}-container`}>
      <Component
        id={id}
        data-testid={id}
        name={name}
        field={field}
        isLoading={isLoading}
      />
    </Box>
  );
};

export const useFormComponent = () => {
  const renderField = isLoading => (
    [name, field]: [string, Field],
    id?: string,
  ) => {
    let Component: any = null;

    switch (field.type) {
      case 'text':
        Component = TextField;
        break;

      case 'display':
        Component = DisplayField;
        break;

      case 'textArea':
        Component = TextAreaField;
        break;

      case 'number':
        Component = NumberField;
        break;

      case 'array':
        Component = ArrayField;
        break;

      case 'object':
        Component = ObjectField;
        break;

      case 'switch':
        Component = SwitchField;
        break;

      case 'checkbox':
        Component = CheckboxField;
        break;

      case 'select':
        Component = SelectField;
        break;

      case 'react-select':
        Component = ReactSelectField;
        break;

      case 'radio':
        Component = RadioField;
        break;

      case 'dayPicker':
        Component = DayPickerField;
        break;

      case 'timePicker':
        Component = TimePickerField;
        break;

      case 'emailIds':
        Component = EmailIDsField;
        break;

      case 'custom-with-form-control':
        Component = CustomField;
        break;

      case 'custom':
        Component = field.component;
        break;

      default:
        break;
    }

    if (!Component) return null;

    return (
      <FieldWrapper
        key={`${name}-wrapper`}
        name={name}
        field={field}
        Component={Component}
        isLoading={isLoading}
        id={id}
      />
    );
  };

  return { renderField };
};
